"use client";

import Image from "next/image";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface ZoomParallaxImage {
  src: string;
  alt?: string;
}

interface ZoomParallaxProps {
  images: ZoomParallaxImage[];
  scrollHeight?: string;
  className?: string;
  overlay?: ReactNode;
}

/** Scroll progress (0–1) when center image reaches full-bleed */
const ZOOM_PHASE_END = 0.52;
/** Scale for center tile to cover hero viewport */
const FILL_SCALE = 4.25;

const POSITION_CLASSES: Record<number, string> = {
  1: "[&>div]:!-top-[30vh] [&>div]:!left-[5vw] [&>div]:!h-[30vh] [&>div]:!w-[35vw] max-md:[&>div]:!-top-[15vh] max-md:[&>div]:!left-[2vw] max-md:[&>div]:!h-[22vh] max-md:[&>div]:!w-[45vw]",
  2: "[&>div]:!-top-[10vh] [&>div]:!-left-[25vw] [&>div]:!h-[45vh] [&>div]:!w-[20vw] max-md:[&>div]:!-top-[5vh] max-md:[&>div]:!-left-[10vw] max-md:[&>div]:!h-[28vh] max-md:[&>div]:!w-[35vw]",
  3: "[&>div]:!left-[27.5vw] [&>div]:!h-[25vh] [&>div]:!w-[25vw] max-md:[&>div]:!left-[10vw] max-md:[&>div]:!h-[20vh] max-md:[&>div]:!w-[35vw]",
  4: "[&>div]:!top-[27.5vh] [&>div]:!left-[5vw] [&>div]:!h-[25vh] [&>div]:!w-[20vw] max-md:[&>div]:!top-[18vh] max-md:[&>div]:!left-[2vw] max-md:[&>div]:!h-[18vh] max-md:[&>div]:!w-[32vw]",
  5: "[&>div]:!top-[27.5vh] [&>div]:!-left-[22.5vw] [&>div]:!h-[25vh] [&>div]:!w-[30vw] max-md:[&>div]:!top-[18vh] max-md:[&>div]:!-left-[8vw] max-md:[&>div]:!h-[18vh] max-md:[&>div]:!w-[38vw]",
  6: "[&>div]:!top-[22.5vh] [&>div]:!left-[25vw] [&>div]:!h-[15vh] [&>div]:!w-[15vw] max-md:[&>div]:!top-[14vh] max-md:[&>div]:!left-[12vw] max-md:[&>div]:!h-[14vh] max-md:[&>div]:!w-[28vw]",
};

const MOTION_LAYER_CLASS =
  "will-change-transform [backface-visibility:hidden] [transform-origin:center]";

export function ZoomParallax({
  images,
  scrollHeight = "150vh",
  className,
  overlay,
}: ZoomParallaxProps) {
  const container = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end start"],
  });

  const zoomProgress = useTransform(
    scrollYProgress,
    [0, ZOOM_PHASE_END],
    [0, 1]
  );

  const outerOpacity = useTransform(
    scrollYProgress,
    [0.28, ZOOM_PHASE_END * 0.9],
    [1, 0]
  );

  const centerScale = useTransform(zoomProgress, [0, 1], [1, FILL_SCALE]);
  const scale4 = useTransform(zoomProgress, [0, 1], [1, 3.2]);
  const scale5 = useTransform(zoomProgress, [0, 1], [1, 3.8]);
  const scale6 = useTransform(zoomProgress, [0, 1], [1, 4]);
  const scale8 = useTransform(zoomProgress, [0, 1], [1, 4.5]);
  const scale9 = useTransform(zoomProgress, [0, 1], [1, 4.8]);

  const outerScales = [scale4, scale5, scale6, scale5, scale6, scale8, scale9];
  const visibleImages = images.slice(0, 7);

  return (
    <div
      ref={container}
      className={cn("relative", className)}
      style={{ height: scrollHeight }}
    >
      <div className="sticky top-[var(--header-offset)] h-[calc(100vh-var(--header-offset))] overflow-hidden bg-black">
        {visibleImages.map(({ src, alt }, index) => {
          const isCenter = index === 0;

          return (
            <motion.div
              key={src}
              style={{
                scale: isCenter
                  ? centerScale
                  : outerScales[index % outerScales.length],
                opacity: isCenter ? 1 : outerOpacity,
                zIndex: isCenter ? 10 : 0,
              }}
              className={cn(
                "absolute inset-0 flex items-center justify-center",
                MOTION_LAYER_CLASS,
                !isCenter && POSITION_CLASSES[index]
              )}
            >
              <div
                className={cn(
                  "relative",
                  isCenter
                    ? "h-[28vh] w-[28vw] max-md:h-[22vh] max-md:w-[44vw]"
                    : "h-[25vh] w-[25vw] max-md:h-[20vh] max-md:w-[40vw]"
                )}
              >
                <Image
                  src={src}
                  alt={alt ?? ""}
                  fill
                  className="object-cover"
                  sizes={isCenter ? "100vw" : "40vw"}
                  priority={index === 0}
                />
              </div>
            </motion.div>
          );
        })}
        {overlay}
      </div>
    </div>
  );
}
